import request from "@/utils/request/request";
import bizlineRequest from "@/utils/request/datalineRequest";
import { TXVSUSRDIVCD } from "@/constant.js";
import dayjs from "dayjs";
import md5 from "js-md5";
import convert from "xml-js";
import { decrypt } from "@/utils/commonUtils.js";

export {
  certificateBizno,
  getCheckplusEncodedData,
  getCheckplusDecodedData,
  updateBizInfo,
  getBizInfoList,
};

/**
 * 사업자등록증명, 표준재무제표증명 증명을 위한 조회
 * @param {*} params
 * @return {data, xmldata}
 */
async function certificateBizno(params) {
  const apiKey = decrypt(process.env.VUE_APP_NICE_API_KEY);
  return await bizlineRequest({
    url: "/v1/ctf/scraping/requests-rt-nts",
    method: "post",
    params: {
      uid: "coseba",
      apikey: md5(apiKey + dayjs().format("YYYYMMDD")),
      output: "json",
    },
    data: {
      ...params,
    },
  }).then(response => {
    return {
      data: response.data.data,
      xmldata: jsonToXml(response.data.data),
    };
  });
}

// 기업정보수정 제출내역 조회
async function getBizInfoList(params) {
  return await bizlineRequest({
    url: "/lookup/taxprove.nice",
    method: "get",
    params: {
      resgp: "TA0108",
      txvscfwissno: params.txvscfwissno,
      startdate: params.startdate,
      enddate: params.enddate,
    },
  }).then(response => {
    const data = getJsonFromResponse(response);
    return data;
  });
}

/**
 * PASS 인증 데이터 생성
 * @return String encodedData
 */
async function getCheckplusEncodedData() {
  return await request({
    url: "/auth/certification/checkplus/encoding",
    method: "get",
    params: {
      sSite: "dataline",
      sReturnUrl: location.origin + "/bizline/checkplus/success",
      sErrorUrl: location.origin + "/bizline/checkplus/fail",
    },
  }).then(response => {
    return response.data;
  });
}

/**
 * PASS 인증 데이터 생성
 * @param {sSite, encodeData} params
 */
async function getCheckplusDecodedData(params) {
  return await request({
    url: "/auth/certification/checkplus/decoding",
    method: "get",
    params: {
      sSite: "dataline",
      encodeData: params.encodeData,
    },
  }).then(response => {
    return response.data;
  });
}

/**
 * 기업정보수정 제출하기 - 수정이지만 쿼리스트링으로 파라미터 붙여야 함
 * @param {*} params
 */
async function updateBizInfo(params) {
  const apiKey = decrypt(process.env.VUE_APP_NICE_API_KEY);
  return await bizlineRequest({
    url: "/v1/ctf/submits/basic-agree",
    method: "post",
    params: {
      uid: "coseba",
      apikey: md5(apiKey + dayjs().format("YYYYMMDD")),
      output: "json",
    },
    data: {
      /* required */
      resgp: "TA0102",
      txvsreqpgmdivcd: params.txvsreqpgmdivcd,
      txvsusrdivcd: getTxvsusrdivcdType(params.bizno),
      cporcd: params.cporcd,
      bizno: params.bizno,
      txvsacqsdatwrkstscd: "10", //세무증명원입수데이터작업상태코드
      txvsitnzdivcd: "10", //세무증명원내재화구분코드
      txtAgree1_text: "Y",
      chkAgree1_yn: "Y",
      chkAgree1_text: "Y",
      txtAgree2_text: "Y",
      chkAgree2_yn: "Y",
      chkAgree2_text: "Y",
      xmldata: params.xmldata,

      /* option */
      regcono: "",
      entrnm: "",
      repr: "",
      tbzacypemgmtno: "",
      empnum: "",
      bizr_addr: "",
      obz_date: "",
      stacmm: "",
      txvsbzdnm: "",
      txvsitemnm: "",
      smt_ofcrnm: params.smt_ofcrnm,
      smt_ofcr_tel: params.smt_ofcr_tel,
      smt_ofcr_eml: "",
      elc_snt_psw_cd_pth: "",
      txvs_itnz_pcs_dtm: "", // 세무증명원내재화처리일시
      hp_certi_string: "",
      org_ofcrnm: "",
      org_chg_tel: "",
      org_chg_eml: "",
      output: "",
    },
  }).then(response => {
    //const data = getJsonFromResponse(response);
    const data = response.data.data;
    if (response.status == 200 && data) {
      return "success";
    } else {
      return "fail";
    }
  });
}

/**
 * 개요 : 사용자가 법인사업자이면 10을 반환, 아니면 20을 반환
 *
 * @bizno 사업자등록번호
 * @return
 */
function getTxvsusrdivcdType(bizno) {
  const type = bizno.substring(3, 5);

  if (isType(type)) {
    return TXVSUSRDIVCD.TXVS_CRP_BIZR;
  } else {
    return TXVSUSRDIVCD.TXVS_PRV_BIZR;
  }
}

/**
 * 개요 : 사용자가 법인사업자이면 10을 반환, 아니면 20을 반환
 *
 * @type 사업자번호 4~5번째 자리
 * @return
 */
function isType(type) {
  const checkType = Number(type);
  if (81 <= checkType && 89 >= checkType) {
    return true;
  } else {
    return false;
  }
}

/**
 * @response axios 응답결과(xml)
 * @return json array 형태로 변환해서 리턴
 */
function getJsonFromResponse(response) {
  const items = response.data.querySelector("Items").children;
  const results = [];
  items.forEach(item => {
    let jsonData = {};
    let attributes = item.getAttributeNames();
    attributes.forEach(attr => (jsonData[attr] = item.getAttribute(attr)));
    results.push(jsonData);
  });

  return results;
}

// JSON을 XML로 변환하는 함수
function jsonToXml(jsonData) {
  // 속성을 동적으로 생성
  const attributes = {};
  Object.keys(jsonData).forEach(key => {
    attributes[key] = jsonData[key];
  });

  const xmlData = {
    item: {
      _attributes: attributes,
    },
  };

  const options = { compact: true, ignoreComment: true, spaces: 4 };
  let xmlString = convert.json2xml(xmlData, options);

  // 단일 태그를 열림 태그와 닫힘 태그로 변경
  xmlString = xmlString.replace(/<item([^>]*)\/>/g, "<item$1></item>");

  return xmlString;
}
